import React, { useState } from 'react'
import { Link } from 'react-router-dom'

const faqs = [
  {
    category: "Orders and Shipping",
    icon: "ph-fill ph-package",
    items: [
      {
        q: "How long does delivery take?",
        a: "Standard delivery takes 5–7 business days. Express delivery (2–3 business days) and same-day delivery (select cities) are also available. You'll receive a tracking link via email once your order is dispatched."
      },
      {
        q: "Can I change or cancel my order after placing it?",
        a: "Orders can be modified or cancelled within 2 hours of placement. After that, our fulfillment process begins and changes may not be possible. Please contact our support team immediately for urgent requests."
      },
      {
        q: "Do you offer free shipping?",
        a: "Yes! Orders above ₹999 qualify for free standard shipping across India. International shipping rates vary by destination and are calculated at checkout."
      },
      {
        q: "How do I track my order?",
        a: "Once shipped, you'll receive an email with your tracking number and a direct link to the courier's tracking page. You can also track your order from the 'My Orders' section in your account."
      }
    ]
  },
  {
    category: "Returns & Refunds",
    icon: "ph-fill ph-arrow-counter-clockwise",
    items: [
      {
        q: "What is your return policy?",
        a: "We offer a hassle-free 30-day return window from the date of delivery. Items must be unused, in original packaging, and accompanied by the original invoice. Certain categories like personalized items are non-returnable."
      },
      {
        q: "How long does a refund take?",
        a: "Once we receive and inspect your return, refunds are processed within 3–5 business days. The amount will be credited to your original payment method. Bank processing times may add 2–3 additional days."
      },
      {
        q: "What if I received a damaged or wrong item?",
        a: "We sincerely apologize for that! Please report the issue within 48 hours of delivery through our support page with photos. We'll arrange an immediate replacement or full refund at no extra cost to you."
      }
    ]
  },
  {
    category: "Payments & Offers",
    icon: "ph-fill ph-credit-card",
    items: [
      {
        q: "What payment methods do you accept?",
        a: "We accept all major credit/debit cards (Visa, Mastercard, RuPay), UPI, Net Banking, and popular wallets like Paytm and PhonePe. EMI options are available on orders above ₹3,000."
      },
      {
        q: "Are my payment details safe?",
        a: "Absolutely. All transactions are secured with 256-bit SSL encryption. We do not store your card details — payments are processed through PCI-DSS compliant payment gateways."
      },
      {
        q: "How do I apply a coupon code?",
        a: "At checkout, you'll find a 'Have a coupon?' field. Enter your code there and click 'Apply'. The discount will reflect instantly on your order total. Only one coupon can be applied per order."
      }
    ]
  },
  {
    category: "Account & Security",
    icon: "ph-fill ph-shield-check",
    items: [
      {
        q: "How do I reset my password?",
        a: "Click 'Forgot Password' on the login page and enter your registered email. You'll receive a secure reset link within minutes. If you don't see it, check your spam folder."
      },
      {
        q: "Can I have multiple addresses saved?",
        a: "Yes! You can save up to 5 delivery addresses in your account under 'Manage Addresses'. You can set one as default and switch between them at checkout."
      }
    ]
  }
]

const FAQItem = ({ q, a }) => {
  const [open, setOpen] = useState(false)
  return (
    <div
      className={`faq-item border border-gray-100 rounded-12 mb-12 overflow-hidden transition-all`}
      style={{
        borderColor: open ? 'var(--main-600, #f5a623)' : undefined,
        boxShadow: open ? '0 4px 24px rgba(245,166,35,0.08)' : undefined
      }}
    >
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: '100%',
          background: open ? 'linear-gradient(90deg, #fff8ed 0%, #fff 100%)' : '#fff',
          border: 'none',
          padding: '20px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          cursor: 'pointer',
          textAlign: 'left',
          transition: 'background 0.2s'
        }}
      >
        <span style={{
          fontFamily: '"Playfair Display", Georgia, serif',
          fontSize: '16px',
          fontWeight: 600,
          color: open ? 'var(--main-600, hsl(var(--main)) !important)' : '#1a1a2e',
          lineHeight: 1.4
        }}>
          {q}
        </span>
        <span style={{
          flexShrink: 0,
          width: 32,
          height: 32,
          borderRadius: '50%',
          background: open ? 'var(--main-600, #f5a623)' : '#f5f5f5',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.25s',
          transform: open ? 'rotate(45deg)' : 'rotate(0deg)'
        }}>
          <i className="ph ph-plus" style={{ fontSize: 16, color: open ? '#fff' : '#666' }} />
        </span>
      </button>
      <div style={{
        maxHeight: open ? '300px' : '0',
        overflow: 'hidden',
        transition: 'max-height 0.35s ease'
      }}>
        <p style={{
          padding: '0 24px 20px',
          margin: 0,
          fontSize: '14.5px',
          color: '#555',
          lineHeight: 1.75,
          fontFamily: '"Lato", sans-serif'
        }}>
          {a}
        </p>
      </div>
    </div>
  )
}

const FAQ = () => {
  const [activeCategory, setActiveCategory] = useState(0)

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700&family=Lato:wght@400;500;700&display=swap');
        :root { --main-600: hsl(var(--main)) !important; }
        .faq-tab-btn { transition: all 0.2s; }
        .faq-tab-btn:hover { background: hsl(25.38deg 100% 94.9%) !important; }
        .faq-tab-btn:hover i { color: hsl(var(--main)) !important !important; }
        .contact-strip a:hover { opacity: 0.85; }
      `}</style>

      <section style={{ padding: '80px 0', background: '#fafaf8' }}>
        <div className="container container-lg">

          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: 56 }}>
            <span style={{
              display: 'inline-block',
              background: 'rgb(255 240 229)',
              color: 'hsl(var(--main)) !important',
              fontFamily: '"Lato", sans-serif',
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              padding: '6px 18px',
              borderRadius: 20,
              marginBottom: 16,
              border: '1px solid #f5e3c0'
            }}>
              Help Center
            </span>
            <h2 style={{
              fontFamily: '"Playfair Display", Georgia, serif',
              fontSize: 'clamp(32px, 5vw, 48px)',
              fontWeight: 700,
              color: '#1a1a2e',
              margin: '0 0 16px'
            }}>
              Frequently Asked Questions
            </h2>
            <p style={{
              fontFamily: '"Lato", sans-serif',
              fontSize: 16,
              color: '#888',
              maxWidth: 520,
              margin: '0 auto',
              lineHeight: 1.7
            }}>
              Everything you need to know about shopping with CoversVilla. Can't find an answer? We're here to help.
            </p>
          </div>

          <div className="row gy-5">
            {/* Sidebar Tabs */}
            <div className="col-lg-4">
              <div style={{
                background: '#fff',
                borderRadius: 16,
                border: '1px solid #ebebeb',
                padding: '8px',
                position: 'sticky',
                top: 24
              }}>
                {faqs.map((cat, idx) => (
                  <button
                    key={idx}
                    className="faq-tab-btn"
                    onClick={() => setActiveCategory(idx)}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 14,
                      padding: '14px 18px',
                      borderRadius: 10,
                      border: 'none',
                      cursor: 'pointer',
                      background: activeCategory === idx ? 'rgb(255 240 229)' : 'transparent',
                      color: activeCategory === idx ? 'hsl(var(--main)) !important' : '#444',
                      fontFamily: '"Lato", sans-serif',
                      fontWeight: activeCategory === idx ? 700 : 500,
                      fontSize: 15,
                      textAlign: 'left',
                      marginBottom: 2,
                      transition: 'all 0.2s'
                    }}
                  >
                    <span style={{
                      width: 38,
                      height: 38,
                      borderRadius: '50%',
                      background: activeCategory === idx ? 'hsl(var(--main)) !important' : '#f5f5f5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      transition: 'all 0.2s'
                    }}>
                      <i className={cat.icon} style={{
                        fontSize: 18,
                        color: activeCategory === idx ? '#fff' : '#888',
                        transition: 'color 0.2s'
                      }} />
                    </span>
                    <span>{cat.category}</span>
                    {activeCategory === idx && (
                      <i className="ph ph-caret-right" style={{ marginLeft: 'auto', fontSize: 14 }} />
                    )}
                  </button>
                ))}

                {/* Contact strip */}
                <div style={{
                  marginTop: 12,
                  background: 'linear-gradient(135deg, #1a1a2e 0%, #2d2d4a 100%)',
                  borderRadius: 12,
                  padding: '20px 18px'
                }}>
                  <p style={{
                    fontFamily: '"Playfair Display", serif',
                    color: '#fff',
                    fontSize: 15,
                    fontWeight: 600,
                    margin: '0 0 4px'
                  }}>Still need help?</p>
                  <p style={{
                    fontFamily: '"Lato", sans-serif',
                    color: '#aaa',
                    fontSize: 13,
                    margin: '0 0 14px',
                    lineHeight: 1.5
                  }}>Our support team is available Mon–Sat, 9am–7pm IST.</p>
                  <Link
                    to="/contact"
                    className="contact-strip"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                      background: 'hsl(var(--main)) !important',
                      color: '#fff',
                      fontFamily: '"Lato", sans-serif',
                      fontWeight: 700,
                      fontSize: 13,
                      padding: '9px 16px',
                      borderRadius: 8,
                      textDecoration: 'none',
                      letterSpacing: '0.03em'
                    }}
                  >
                    <i className="ph ph-headset" style={{ fontSize: 15 }} />
                    Contact Support
                  </Link>
                </div>
              </div>
            </div>

            {/* FAQ Items */}
            <div className="col-lg-8">
              <div style={{
                background: '#fff',
                borderRadius: 16,
                border: '1px solid #ebebeb',
                padding: '32px 28px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 28 }}>
                  <span style={{
                    width: 44,
                    height: 44,
                    borderRadius: '50%',
                    background: '#fff8ed',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <i className={faqs[activeCategory].icon} style={{ fontSize: 22, color: 'hsl(var(--main)) !important' }} />
                  </span>
                  <div>
                    <h6 style={{
                      fontFamily: '"Playfair Display", serif',
                      fontSize: 20,
                      fontWeight: 700,
                      color: '#1a1a2e',
                      margin: 0
                    }}>
                      {faqs[activeCategory].category}
                    </h6>
                    <span style={{
                      fontFamily: '"Lato", sans-serif',
                      fontSize: 13,
                      color: '#aaa'
                    }}>
                      {faqs[activeCategory].items.length} questions
                    </span>
                  </div>
                </div>

                {faqs[activeCategory].items.map((item, idx) => (
                  <FAQItem key={`${activeCategory}-${idx}`} q={item.q} a={item.a} />
                ))}
              </div>

              {/* Bottom CTA */}
              <div className='bg-main-two-50' style={{
                marginTop: 24,
               
                border: '1px solid #f5e3c0',
                borderRadius: 16,
                padding: '28px 28px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 16
              }}>
                <div className='' > 
                  <h6 style={{
                    fontFamily: '"Playfair Display", serif',
                    fontSize: 18,
                    fontWeight: 700,
                    color: '#1a1a2e',
                    margin: '0 0 4px'
                  }}>
                    Didn't find what you were looking for?
                  </h6>
                  <p style={{
                    fontFamily: '"Lato", sans-serif',
                    fontSize: 14,
                    color: '#888',
                    margin: 0
                  }}>
                    Reach out and we'll get back to you within 24 hours.
                  </p>
                </div>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <Link
                    to="/contact"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                      background: '#1a1a2e',
                      color: '#fff',
                      fontFamily: '"Lato", sans-serif',
                      fontWeight: 700,
                      fontSize: 14,
                      padding: '12px 22px',
                      borderRadius: 8,
                      textDecoration: 'none'
                    }}
                  >
                    <i className="ph ph-envelope" style={{ fontSize: 16 }} />
                    Email Us
                  </Link>
                  <Link
                    to="/tel:+00123456789"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                      background: 'hsl(var(--main)) !important',
                      color: '#fff',
                      fontFamily: '"Lato", sans-serif',
                      fontWeight: 700,
                      fontSize: 14,
                      padding: '12px 22px',
                      borderRadius: 8,
                      textDecoration: 'none'
                    }}
                  >
                    <i className="ph ph-phone-call" style={{ fontSize: 16 }} />
                    Call Us
                  </Link>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>
    </>
  )
}

export default FAQ